import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Target, Calendar, AlertTriangle,
  Loader2, Wallet, Receipt, Activity, ArrowRight, RefreshCw,
} from "lucide-react";

const CAD = (v) =>
  new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v || 0);

const CAD2 = (v) =>
  new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const CAT_LABEL = {
  "Revenu Paiement CC (Paysafe/Stripe)": "💳 Paiements CC (Stripe)",
  "Virement Interac": "📩 Interac",
  "Revenu / Dépôt direct": "💵 Dépôt direct",
  "Revenu Impact Elite": "🥋 Impact Elite",
  "Équipement sportif": "🥊 Équipement sportif",
  "Matériel / Fournitures": "📦 Matériel / Fournitures",
  "Dépôt en caisse": "🏦 Dépôt caisse",
  "non_classe": "❓ Non classé",
};

const labelize = (c) => CAT_LABEL[c] || c.replace(/_/g, " ").replace(/^./, (m) => m.toUpperCase());


export default function PnLLiveModule() {
  const [account, setAccount] = useState("compagnie");
  const [dashboard, setDashboard] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [debtConfig, setDebtConfig] = useState({ total_debt: 150000, monthly_min_required: 0, target_date: "", notes: "" });
  const [projection, setProjection] = useState(null);
  const [extraInput, setExtraInput] = useState(0);
  const [loading, setLoading] = useState(false);
  const [includeZp, setIncludeZp] = useState(true);
  const [gala, setGala] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m, c, g] = await Promise.all([
        api.get(`/pnl/dashboard?account=${account}&include_zp=${includeZp}`),
        api.get(`/pnl/monthly?months=12&account=${account}&include_zp=${includeZp}`),
        api.get(`/pnl/debt-config`).catch(() => ({ data: { total_debt: 0 } })),
        api.get(`/factures-impayees/gala-schedule`).catch(() => ({ data: null })),
      ]);
      setDashboard(d.data);
      setMonthly(m.data.series);
      setGala(g.data && g.data.count ? g.data : null);
      if (c.data?.total_debt) setDebtConfig({
        total_debt: c.data.total_debt,
        target_date: c.data.target_date || "",
        monthly_min_required: c.data.monthly_min_required || 0,
        notes: c.data.notes || "",
      });
    } catch (e) {
      toast.error("Erreur chargement P&L : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [account, includeZp]);

  useEffect(() => { load(); }, [load]);

  const saveDebt = async () => {
    try {
      await api.post(`/pnl/debt-config`, {
        total_debt: Number(debtConfig.total_debt) || 0,
        target_date: debtConfig.target_date || null,
        monthly_min_required: Number(debtConfig.monthly_min_required) || 0,
        notes: debtConfig.notes || null,
      });
      toast.success("Dette configurée");
      load();
    } catch (e) {
      toast.error("Erreur sauvegarde dette");
    }
  };

  const runProjection = async (extra = 0) => {
    try {
      const r = await api.get(`/pnl/debt-projection?extra_monthly=${extra}`);
      setProjection(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur projection");
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="pnl-loading">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!dashboard) return null;

  const cm = dashboard.current_month;
  const ytd = dashboard.ytd;
  const rr = dashboard.run_rate;
  const debt = dashboard.debt;

  const maxRev = Math.max(...monthly.map(m => m.revenu), 1);
  const maxDep = Math.max(...monthly.map(m => m.depense), 1);
  const chartMax = Math.max(maxRev, maxDep);

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="pnl-live-module">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-amber-100 tracking-tight">
            P&L Live · Boussole Cash Flow
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Croise tes 4930 transactions bancaires avec tes 585 factures comptabilité pour visualiser ta route vers zéro dette.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIncludeZp(v => !v)}
            data-testid="pnl-toggle-zp"
            title="Inclure les revenus historiques Zen Planner"
            className={`px-3 py-2 rounded-lg text-sm font-semibold border transition ${
              includeZp
                ? "bg-emerald-600/20 border-emerald-600 text-emerald-300"
                : "bg-zinc-900 border-zinc-700 text-zinc-400"
            }`}
          >
            {includeZp ? "✓ Revenus ZP inclus" : "Revenus ZP exclus"}
          </button>
          <select
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 text-amber-100 rounded-lg px-3 py-2 text-sm"
            data-testid="pnl-account-select"
          >
            <option value="compagnie">Compagnie (Impact)</option>
            <option value="perso">Perso</option>
            <option value="">Tout</option>
          </select>
          <button onClick={load} className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-amber-400" data-testid="pnl-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Sources de revenus */}
      {dashboard.revenue_sources_ytd && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4" data-testid="pnl-revenue-sources">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Sources de revenus YTD ({ytd.year})</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SourceChip label="Transactions bancaires" amount={dashboard.revenue_sources_ytd.bank_transactions} color="sky" />
            <SourceChip
              label="Zen Planner (historique)"
              amount={dashboard.revenue_sources_ytd.zen_planner_historique}
              color="emerald"
              muted={!dashboard.revenue_sources_ytd.zp_included}
            />
            <SourceChip label="Revenu total" amount={ytd.revenu} color="amber" />
          </div>
          {!dashboard.revenue_sources_ytd.zp_included && (
            <p className="text-xs text-amber-400/80 mt-2">⚠️ Revenus ZP exclus du calcul (active le bouton « Revenus ZP inclus » ou choisis Compagnie/Tout).</p>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Mois en cours"
          sub={cm.ym}
          value={CAD(cm.net)}
          delta={cm.revenu - cm.depense >= 0 ? "positif" : "deficitaire"}
          rev={cm.revenu}
          dep={cm.depense}
          icon={Activity}
          accent={cm.net >= 0 ? "amber" : "red"}
          testid="kpi-current-month"
        />
        <KpiCard
          label="YTD (depuis 1er janvier)"
          sub={`${ytd.months_so_far} mois`}
          value={CAD(ytd.net)}
          delta={ytd.net >= 0 ? "+" : ""}
          rev={ytd.revenu}
          dep={ytd.depense}
          icon={Calendar}
          accent={ytd.net >= 0 ? "amber" : "red"}
          testid="kpi-ytd"
        />
        <KpiCard
          label="Run-rate annuel (12m)"
          sub="Projection 12 prochains mois"
          value={CAD(rr.annual_net_projection)}
          rev={rr.annual_rev_projection}
          dep={rr.annual_dep_projection}
          icon={TrendingUp}
          accent={rr.annual_net_projection >= 0 ? "amber" : "red"}
          testid="kpi-runrate"
        />
        <div className={`rounded-2xl p-5 border ${debt.months_to_zero_at_current_rate && debt.months_to_zero_at_current_rate > 0 ? "border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-zinc-900" : "border-red-500/30 bg-gradient-to-br from-red-500/10 to-zinc-900"}`}
             data-testid="kpi-debt">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-zinc-400">Dette à éteindre</span>
            <Target className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-bold text-amber-100">{CAD(debt.total_debt)}</div>
          <div className="text-xs text-zinc-500 mt-2">
            {debt.months_to_zero_at_current_rate === null && "Configure ta dette →"}
            {debt.months_to_zero_at_current_rate === -1 && (
              <span className="text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Cash flow négatif — dette ne diminue pas
              </span>
            )}
            {debt.months_to_zero_at_current_rate > 0 && (
              <span className="text-amber-300">
                ETA : {debt.months_to_zero_at_current_rate} mois au rythme actuel
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Chart */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-amber-100">12 derniers mois — Revenus vs Dépenses</h2>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" />Revenus</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/70" />Dépenses</span>
            <span className="flex items-center gap-1"><span className="w-3 h-1 rounded bg-amber-400" />Net cumulé</span>
          </div>
        </div>
        <div className="flex items-end gap-2 h-48 overflow-x-auto pb-2" data-testid="pnl-monthly-chart">
          {monthly.map((m) => {
            const hR = (m.revenu / chartMax) * 100;
            const hD = (m.depense / chartMax) * 100;
            return (
              <div key={m.month} className="flex-shrink-0 flex flex-col items-center gap-1 w-16" data-testid={`pnl-bar-${m.month}`}>
                <div className="flex items-end gap-0.5 h-40 w-full justify-center">
                  <div
                    className="w-5 bg-emerald-500 rounded-t hover:bg-emerald-400 transition-colors"
                    style={{ height: `${hR}%` }}
                    title={`Revenus ${m.month}: ${CAD2(m.revenu)}`}
                  />
                  <div
                    className="w-5 bg-red-500/70 rounded-t hover:bg-red-400 transition-colors"
                    style={{ height: `${hD}%` }}
                    title={`Dépenses ${m.month}: ${CAD2(m.depense)}`}
                  />
                </div>
                <div className="text-[10px] text-zinc-500 font-mono">{m.month.slice(5)}</div>
                <div className={`text-[10px] font-semibold ${m.net >= 0 ? "text-amber-300" : "text-red-400"}`}>
                  {m.net >= 0 ? "+" : ""}{CAD(m.net)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cashflow gala — impact des dettes du gala sur la Boussole */}
      {gala && (
        <div className="rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-500/5 to-zinc-950 p-5" data-testid="pnl-gala-cashflow">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-red-200 flex items-center gap-2">
                🥊 Impact des dettes du gala sur le cash flow
              </h2>
              <p className="text-xs text-zinc-500 mt-1">
                Gala Gladiateur du Nord · {gala.count} créancier(s) · plans de remboursement mensuels
              </p>
            </div>
            <a href="/app/factures-impayees" className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1" data-testid="pnl-gala-link">
              Gérer les dettes <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Alerte mois serrés : paiement gala > cash net mensuel disponible */}
          {(() => {
            const monthlyNet = rr?.annual_net_projection ? rr.annual_net_projection / 12
              : (ytd?.months_so_far ? ytd.net / ytd.months_so_far : 0);
            const now = new Date().toISOString().slice(0, 7);
            const tight = (gala.schedule || []).filter(s => s.month >= now && monthlyNet > 0 && s.amount > monthlyNet);
            if (monthlyNet <= 0) {
              return (
                <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 flex items-start gap-2" data-testid="gala-cashflow-alert">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <div className="text-xs text-red-200">
                    <strong>Cash flow mensuel négatif ou nul.</strong> Chaque paiement du gala pèse directement sur ta trésorerie — priorise les arrangements les plus souples.
                  </div>
                </div>
              );
            }
            if (tight.length === 0) return null;
            return (
              <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-2" data-testid="gala-cashflow-alert">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-200">
                  <strong>{tight.length} mois serré(s) à anticiper.</strong> Le paiement du gala dépasse ton cash net mensuel estimé ({CAD(monthlyNet)}/mois) en :{" "}
                  {tight.map((t, i) => (
                    <span key={t.month} className="font-mono">{t.month} ({CAD(t.amount)}){i < tight.length - 1 ? ", " : ""}</span>
                  ))}. Prévois une réserve ou renégocie l'échéancier de ces mois.
                </div>
              </div>
            );
          })()}

          {/* KPIs gala */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-lg border border-red-700/40 bg-zinc-950/40 px-4 py-3" data-testid="gala-remaining">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">Reste à rembourser</div>
              <div className="text-xl font-bold text-red-300 mt-0.5">{CAD2(gala.remaining)}</div>
              <div className="text-[10px] text-zinc-600 mt-0.5">sur {CAD2(gala.total_debt)} au total</div>
            </div>
            <div className="rounded-lg border border-amber-600/40 bg-zinc-950/40 px-4 py-3" data-testid="gala-next-payment">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">Prochain paiement dû</div>
              {gala.next_payment ? (
                <>
                  <div className="text-xl font-bold text-amber-300 mt-0.5">{CAD2(gala.next_payment.amount)}</div>
                  <div className="text-[10px] text-zinc-600 mt-0.5 font-mono">{gala.next_payment.month}</div>
                </>
              ) : (
                <div className="text-sm text-emerald-300 mt-1">Aucun à venir ✓</div>
              )}
            </div>
            <div className="rounded-lg border border-zinc-700/50 bg-zinc-950/40 px-4 py-3" data-testid="gala-end-month">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">Fin de remboursement</div>
              <div className="text-xl font-bold text-zinc-200 mt-0.5 font-mono">{gala.end_month || "—"}</div>
              <div className="text-[10px] text-zinc-600 mt-0.5">dernière échéance prévue</div>
            </div>
          </div>

          {/* Échéancier mois-par-mois */}
          {gala.schedule && gala.schedule.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">
                Échéancier mensuel — sortie de cash à prévoir
              </div>
              {(() => {
                const now = new Date().toISOString().slice(0, 7);
                const gMax = Math.max(...gala.schedule.map(s => s.amount), 1);
                return (
                  <div className="flex items-end gap-2 h-40 overflow-x-auto pb-2" data-testid="gala-schedule-chart">
                    {gala.schedule.map((s) => {
                      const isPast = s.month < now;
                      const isCurrent = s.month === now;
                      return (
                        <div key={s.month} className="flex-shrink-0 flex flex-col items-center gap-1 w-16" data-testid={`gala-bar-${s.month}`}>
                          <div className="flex items-end h-28 w-full justify-center">
                            <div
                              className={`w-8 rounded-t transition-colors ${
                                isCurrent ? "bg-amber-400" : isPast ? "bg-zinc-700" : "bg-red-500/70 hover:bg-red-400"
                              }`}
                              style={{ height: `${(s.amount / gMax) * 100}%` }}
                              title={`${s.month} : ${CAD2(s.amount)}`}
                            />
                          </div>
                          <div className={`text-[10px] font-mono ${isCurrent ? "text-amber-300" : "text-zinc-500"}`}>{s.month.slice(2)}</div>
                          <div className={`text-[10px] font-semibold ${isPast ? "text-zinc-600" : "text-red-300"}`}>{CAD(s.amount)}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="flex items-center gap-3 text-[10px] text-zinc-500 mt-2">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400" />Mois en cours</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/70" />À venir</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-zinc-700" />Passé</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Categories side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-emerald-500/20 bg-zinc-950 p-5">
          <h3 className="text-md font-semibold text-emerald-300 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Top revenus YTD ({ytd.year})
          </h3>
          <ul className="space-y-2" data-testid="top-revenus">
            {dashboard.top_categories_revenu_ytd.map((c) => (
              <li key={c.category} className="flex justify-between items-center text-sm border-b border-zinc-800/50 pb-1.5">
                <span className="text-zinc-300">{labelize(c.category)}</span>
                <span className="text-emerald-300 font-mono">{CAD2(c.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-red-500/20 bg-zinc-950 p-5">
          <h3 className="text-md font-semibold text-red-300 mb-3 flex items-center gap-2">
            <TrendingDown className="w-4 h-4" /> Top dépenses YTD ({ytd.year})
          </h3>
          <ul className="space-y-2" data-testid="top-depenses">
            {dashboard.top_categories_depense_ytd.map((c) => (
              <li key={c.category} className="flex justify-between items-center text-sm border-b border-zinc-800/50 pb-1.5">
                <span className="text-zinc-300">{labelize(c.category)}</span>
                <span className="text-red-300 font-mono">{CAD2(c.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Debt Config + Simulator */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-zinc-950 p-5" data-testid="debt-config">
          <h3 className="text-md font-semibold text-amber-200 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4" /> Configure ta dette à éteindre
          </h3>
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs text-zinc-400">Solde total dette (CAD)</span>
              <input
                type="number"
                value={debtConfig.total_debt}
                onChange={(e) => setDebtConfig({ ...debtConfig, total_debt: e.target.value })}
                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 text-sm"
                data-testid="debt-total-input"
              />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">Paiement minimum mensuel obligatoire</span>
              <input
                type="number"
                value={debtConfig.monthly_min_required}
                onChange={(e) => setDebtConfig({ ...debtConfig, monthly_min_required: e.target.value })}
                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 text-sm"
                data-testid="debt-min-input"
              />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">Date cible (optionnel)</span>
              <input
                type="date"
                value={debtConfig.target_date}
                onChange={(e) => setDebtConfig({ ...debtConfig, target_date: e.target.value })}
                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 text-sm"
                data-testid="debt-target-input"
              />
            </label>
            <button
              onClick={saveDebt}
              className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold py-2 rounded-lg transition"
              data-testid="save-debt-btn"
            >
              Sauvegarder
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5" data-testid="debt-simulator">
          <h3 className="text-md font-semibold text-amber-200 mb-3 flex items-center gap-2">
            <Wallet className="w-4 h-4" /> Simulateur paydown
          </h3>
          <div className="flex items-center gap-2 mb-4">
            <input
              type="number"
              placeholder="Cash supplémentaire / mois"
              value={extraInput}
              onChange={(e) => setExtraInput(Number(e.target.value) || 0)}
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 text-sm"
              data-testid="debt-extra-input"
            />
            <button
              onClick={() => runProjection(extraInput)}
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-1"
              data-testid="run-projection-btn"
            >
              Simuler <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {projection && (
            <div className="space-y-2 mt-3" data-testid="projection-results">
              <div className="text-xs text-zinc-500 border-b border-zinc-800 pb-1">
                Dette {CAD(projection.current_debt)} · Net mensuel actuel {CAD(projection.current_avg_monthly_net)}
              </div>
              {projection.scenarios.map((s) => (
                <div key={s.label} className="flex items-center justify-between text-sm py-1.5 border-b border-zinc-800/40">
                  <span className="text-zinc-300 capitalize">{s.label.replace("_", " ")}</span>
                  <div className="text-right">
                    <div className="text-amber-300 font-mono text-xs">+{CAD(s.monthly_paydown)}/mois</div>
                    {s.feasible ? (
                      <div className="text-emerald-300 text-xs">
                        {s.months_to_zero} mois → {s.eta_date}
                      </div>
                    ) : (
                      <div className="text-red-400 text-xs flex items-center gap-1 justify-end">
                        <AlertTriangle className="w-3 h-3" /> impossible (cash flow ≤ 0)
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer note */}
      <div className="text-xs text-zinc-500 text-center border-t border-zinc-800 pt-3">
        Sources : <code className="text-amber-400">transactions</code> (relevés bancaires) ·{" "}
        <code className="text-amber-400">bookkeeping_receipts</code> (WellyBox · Gemini Vision).
        Les transactions "TEST" sont exclues automatiquement.
      </div>
    </div>
  );
}


function KpiCard({ label, sub, value, rev, dep, icon: Icon, accent, testid }) {
  const accentMap = {
    amber: "border-amber-500/30 from-amber-500/10",
    red: "border-red-500/30 from-red-500/10",
    emerald: "border-emerald-500/30 from-emerald-500/10",
  };
  return (
    <div className={`rounded-2xl p-5 border bg-gradient-to-br to-zinc-900 ${accentMap[accent] || accentMap.amber}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest text-zinc-400">{label}</span>
        <Icon className="w-4 h-4 text-amber-400" />
      </div>
      <div className="text-2xl font-bold text-amber-100">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-1 font-mono">{sub}</div>
      {(rev !== undefined || dep !== undefined) && (
        <div className="mt-3 flex gap-3 text-[11px]">
          <div className="text-emerald-400">↑ {CAD(rev)}</div>
          <div className="text-red-400">↓ {CAD(dep)}</div>
        </div>
      )}
    </div>
  );
}


function SourceChip({ label, amount, color, muted }) {
  const colorMap = {
    sky: "text-sky-300 border-sky-700/50",
    emerald: "text-emerald-300 border-emerald-700/50",
    amber: "text-amber-300 border-amber-600/50",
  };
  return (
    <div className={`rounded-lg border bg-zinc-950/40 px-4 py-3 ${colorMap[color]} ${muted ? "opacity-40" : ""}`}>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-lg font-bold mt-0.5">{CAD(amount)}</div>
    </div>
  );
}
